// inv/src/components/InvestorsFilter.js

import { useState, useEffect, useRef } from 'react';
import { gaTrack, sanitizeQuery } from '../../../service-scripts/gaTrack';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../../../config';
import getUniqueInvestorTypes from './investorsTypes';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { InputSearch } from '../../ui/inputSearch';
import { AlertBox } from '../../ui/alertBox';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { useTranslation } from 'react-i18next';

const DEBOUNCE_RESULTS_MS = 600;     // to avoid sending results_update too often
const INACTIVITY_MS = 20000;         // 20 seconds of inactivity = filter commit (one event)
const RESULTS_MIN_INTERVAL_MS = 2000; // results_update no more than once every 2 seconds
const COMMIT_MIN_INTERVAL_MS = 3000;  // filters_commit no more than once every 3 seconds
const APPLY_DEDUP_MS = 300;           // protection against Enter + onAddValue, etc.

function cleanRemarketingValue(value, max = 100) {
  if (!value || typeof value !== 'string') return '';
  return sanitizeQuery(value)
    .trim()
    .slice(0, max);
}

function resultsBucket(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'unknown';
  if (n <= 0) return '0';
  if (n <= 10) return '1_10';
  if (n <= 50) return '11_50';
  if (n <= 250) return '51_250';
  if (n <= 1000) return '251_1000';
  return '1001_plus';
}

function intentLevel({ filterCount, resultsCount }) {
  const results = Number(resultsCount);
  if (filterCount >= 3) return 'high';
  if (filterCount >= 2 && Number.isFinite(results) && results > 0) return 'high';
  if (filterCount >= 2) return 'medium';
  if (filterCount === 1) return 'medium';
  return 'low';
}

function buildSearchIntentPayload(payload) {
  const keyword = cleanRemarketingValue(payload.keyword_applied || payload.keyword_current, 80);
  const country = cleanRemarketingValue(payload.country_applied || payload.country_current, 80);
  const industry = cleanRemarketingValue(payload.industry_applied || payload.industry_current, 100);
  const types = Array.isArray(payload.investor_types_list)
    ? payload.investor_types_list.map(type => cleanRemarketingValue(type, 80)).filter(Boolean)
    : [];

  const filterCount = [
    keyword,
    country,
    industry,
    types.length > 0,
  ].filter(Boolean).length;

  if (!filterCount) return null;

  return {
    context: 'investors',
    fi_keyword: keyword,
    fi_country: country,
    fi_industry: industry,
    fi_primary_type: types[0] || '',
    fi_types: types.slice(0, 5).join('|'),
    fi_types_count: types.length,
    fi_filter_count: filterCount,
    fi_results_bucket: resultsBucket(payload.results_count),
    fi_intent_level: intentLevel({ filterCount, resultsCount: payload.results_count }),
    results_count: Number.isFinite(Number(payload.results_count)) ? Number(payload.results_count) : null,
  };
}

const InvestorsFilter = ({
  onLocationChange,
  onIndustriesChange,
  onKeywordChange,
  totalResults,
  onUpdateTypes,
}) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const formatTotalCount = (n) => (n ? Number(n).toLocaleString() : '');

  // --- internal refs/timers ---
  const suppressGA = useRef(false);          // mute GA during hydration from the URL
  const resultsTimer = useRef(null);         // debounce sending results_update
  const commitTimer = useRef(null);          // 20-second inactivity countdown
  const lastParamsStrRef = useRef('');       // prevents extra navigate calls
  const hasUserInteracted = useRef(false);
  const commitGen = useRef(0);               // active timer version

  // dedupe for results_update
  const lastResultsHashRef = useRef('');
  const lastResultsSentAtRef = useRef(0);

  // dedupe for filters_commit
  const lastCommitHashRef = useRef('');
  const lastCommitSentAtRef = useRef(0);

  // dedupe applied filters
  const lastApplyRef = useRef({
    keyword: { v: '', t: 0 },
    industry: { v: '', t: 0 },
    country: { v: '', t: 0 },
  });

  function shouldApplyOnce(key, value) {
    const now = Date.now();
    const rec = lastApplyRef.current[key];
    if (rec && rec.v === value && now - rec.t < APPLY_DEDUP_MS) return false;
    lastApplyRef.current[key] = { v: value, t: now };
    return true;
  }

  // --- live input values (even if not applied) ---
  const [liveKeyword, setLiveKeyword] = useState('');
  const [liveIndustry, setLiveIndustry] = useState('');
  const [liveCountry, setLiveCountry] = useState('');

  // --- reference lists and selected values ---
  const [investorTypes, setInvestorTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showAllTypes, setShowAllTypes] = useState(false);

  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [appliedLocation, setAppliedLocation] = useState('');
  const [appliedIndustries, setAppliedIndustries] = useState('');

  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ---------- reference data initialization ----------
  useEffect(() => {
    (async () => {
      try {
        const unique = await getUniqueInvestorTypes();
        setInvestorTypes(unique || []);
      } catch (e) {
        console.error('Error fetching investor types:', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/investors/filters?fields=country`,
          { credentials: 'include' } // equivalent of axios.withCredentials
        );
        if (!res.ok) {
          console.error('Countries request failed:', res.status, res.statusText);
          setCountries([]);
          return;
        }
        const data = await res.json();
        const countries = Array.isArray(data?.country?.values) ? data.country.values : [];
        setCountries(countries);
      } catch (e) {
        console.error('Error fetching countries:', e);
        setCountries([]);
      }
    })();
  }, []);

  // ---------- read filters from URL (ONCE on mount) ----------
  useEffect(() => {
    suppressGA.current = true;

    const params = new URLSearchParams(search);
    const urlTypes = params.get('types');
    const urlCountry = params.get('country');
    const urlKeyword = params.get('keyword');
    const urlIndustries = params.get('industries');

    const nextTypes = urlTypes ? urlTypes.split(',').filter(Boolean) : [];
    setSelectedTypes(nextTypes);
    onUpdateTypes(nextTypes);

    setAppliedLocation(urlCountry || '');
    onLocationChange(urlCountry || '');

    setAppliedKeyword(urlKeyword || '');
    onKeywordChange(urlKeyword || '');

    setAppliedIndustries(urlIndustries || '');
    onIndustriesChange(urlIndustries || '');

    // sync live fields with applied values
    setLiveKeyword(urlKeyword || '');
    setLiveCountry(urlCountry || '');
    setLiveIndustry(urlIndustries || '');
    const initialParams = new URLSearchParams(search);
    initialParams.delete('page');
    lastParamsStrRef.current = initialParams.toString();

    // release the flag without starting the timer (it will start only after user interaction)
    queueMicrotask(() => {
      suppressGA.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- keep URL up to date ----------
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTypes.length) params.set('types', selectedTypes.join(','));
    if (appliedLocation) params.set('country', appliedLocation);
    if (appliedKeyword) params.set('keyword', appliedKeyword);
    if (appliedIndustries) params.set('industries', appliedIndustries);

    const nextParamsStr = params.toString();                  // without "?"
    const currentParams = new URLSearchParams(search);
    currentParams.delete('page');
    const currParamsStr = currentParams.toString();

    // do not modify the URL during hydration and before the first user action
    if (suppressGA.current || !hasUserInteracted.current) return;

    const nextUrl = nextParamsStr ? `${pathname}?${nextParamsStr}` : pathname;

    if (currParamsStr === nextParamsStr) return;

    if (lastParamsStrRef.current !== nextParamsStr) {
      lastParamsStrRef.current = nextParamsStr;
      navigate(nextUrl, { replace: true });
    }
  }, [
    selectedTypes,
    appliedLocation,
    appliedKeyword,
    appliedIndustries,
    pathname,
    search,
    navigate,
  ]);

  // clear timers
  useEffect(() => {
    return () => {
      if (resultsTimer.current) clearTimeout(resultsTimer.current);
      if (commitTimer.current) clearTimeout(commitTimer.current);
    };
  }, []);

  // reset loading state when totalResults is updated
  useEffect(() => {
    if (Number.isFinite(Number(totalResults))) {
      setIsLoading(false);
    }
  }, [totalResults]);

  // ---------- results_update (debounce + dedup) ----------
  useEffect(() => {
    if (suppressGA.current) return;
    if (!Number.isFinite(Number(totalResults))) return;

    if (resultsTimer.current) clearTimeout(resultsTimer.current);
    resultsTimer.current = setTimeout(() => {
      const payload = {
        context: 'investors',
        results_count: Number(totalResults),
        has_keyword: !!appliedKeyword,
        has_country: !!appliedLocation,
        has_industry: !!appliedIndustries,
        investor_types_count: selectedTypes.length,
        investor_types_list: selectedTypes.slice(0, 25),
      };
      const hash = JSON.stringify(payload);
      const now = Date.now();
      const tooSoon = now - lastResultsSentAtRef.current < RESULTS_MIN_INTERVAL_MS;

      if (hash !== lastResultsHashRef.current || !tooSoon) {
        gaTrack('results_update', payload);
        lastResultsHashRef.current = hash;
        lastResultsSentAtRef.current = now;
      }
    }, DEBOUNCE_RESULTS_MS);

    return () => {
      if (resultsTimer.current) clearTimeout(resultsTimer.current);
    };
  }, [
    totalResults,
    appliedKeyword,
    appliedLocation,
    appliedIndustries,
    selectedTypes.join('|'), // composition matters more than length
  ]);

  // ---------- global 20-second commit after the last action ----------
  const restartCommitCountdown = () => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    if (suppressGA.current) return;
    const myGen = ++commitGen.current;
    commitTimer.current = setTimeout(() => {
      if (myGen !== commitGen.current) return;

      const payload = {
        context: 'investors',
        // current (entered) field state — even if Enter was not pressed
        keyword_current: sanitizeQuery(liveKeyword || ''),
        country_current: liveCountry || '',
        industry_current: liveIndustry || '',
        // applied state (what actually filters)
        keyword_applied: appliedKeyword || '',
        country_applied: appliedLocation || '',
        industry_applied: appliedIndustries || '',
        investor_types_list: selectedTypes.slice(0, 25),
        investor_types_count: selectedTypes.length,
        results_count: Number.isFinite(Number(totalResults)) ? Number(totalResults) : null,
      };

      const hash = JSON.stringify(payload);
      const now = Date.now();
      const tooSoon = now - lastCommitSentAtRef.current < COMMIT_MIN_INTERVAL_MS;

      if (hash !== lastCommitHashRef.current || !tooSoon) {
        gaTrack('filters_commit', payload);
        const searchIntent = buildSearchIntentPayload(payload);
        if (searchIntent) {
          gaTrack('investor_search_intent', searchIntent);
        }
        lastCommitHashRef.current = hash;
        lastCommitSentAtRef.current = now;
      }
    }, INACTIVITY_MS);
  };

  // ---------- APPLY/CLEAR ----------
  const handleTypeChange = (checked, type) => {
    hasUserInteracted.current = true;
    setIsLoading(true);
    setSelectedTypes((prev) => {
      const next = checked ? [...prev, type] : prev.filter((t) => t !== type);
      onUpdateTypes(next); // instant filtering as before
      restartCommitCountdown();
      return next;
    });
  };

  const applyKeyword = (raw) => {
    hasUserInteracted.current = true;
    const value = sanitizeQuery(raw || '');
    if (!value) return clearKeyword();
    if (!shouldApplyOnce('keyword', value)) return;
    setIsLoading(true);
    setAppliedKeyword(value);
    onKeywordChange(value);
    setLiveKeyword(value);
    restartCommitCountdown();
  };

  const clearKeyword = () => {
    hasUserInteracted.current = true;
    setAppliedKeyword('');
    onKeywordChange('');
    setLiveKeyword('');
    restartCommitCountdown();
  };

  const applyIndustry = (raw) => {
    hasUserInteracted.current = true;
    const value = (raw || '').trim();
    if (!value) return clearIndustry();
    if (!shouldApplyOnce('industry', value)) return;
    setIsLoading(true);
    setAppliedIndustries(value);
    onIndustriesChange(value);
    setLiveIndustry(value);
    restartCommitCountdown();
  };

  const clearIndustry = () => {
    hasUserInteracted.current = true;
    setAppliedIndustries('');
    onIndustriesChange('');
    setLiveIndustry('');
    restartCommitCountdown();
  };

  const applyLocation = (country) => {
    hasUserInteracted.current = true;
    const val = (country ?? '').trim();
    if (!val) return clearLocation();
    if (!shouldApplyOnce('country', val)) return;
    setIsLoading(true);
    setAppliedLocation(val);
    onLocationChange(val);
    setLiveCountry(val);
    restartCommitCountdown();
  };

  const clearLocation = () => {
    hasUserInteracted.current = true;
    setAppliedLocation('');
    onLocationChange('');
    setLiveCountry('');
    restartCommitCountdown();
  };

  const resetFilters = () => {
    hasUserInteracted.current = true;
    setSelectedTypes([]);
    onUpdateTypes([]);
    setAppliedKeyword('');
    setAppliedLocation('');
    setAppliedIndustries('');
    onKeywordChange('');
    onLocationChange('');
    onIndustriesChange('');
    setLiveKeyword('');
    setLiveCountry('');
    setLiveIndustry('');
    restartCommitCountdown();
  };

  // ---------- RENDER ----------
  return (
    <>
      {/* total results */}
      <div>
        <span className="font-bold">{t('pageFindInvestors.fltrtotRslts')}</span>{' '}
        {isLoading ? (
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-stone-900 mr-2"></div>
          </div>
        ) : (
          formatTotalCount(totalResults)
        )}
        <a
          href="https://www.producthunt.com/products/investor-database?utm_source=badge-follow&utm_medium=badge&utm_source=badge-investor&#0045;database"
          target="_blank"
        >
          <img
            src="https://api.producthunt.com/widgets/embed-image/v1/follow.svg?product_id=309743&theme=light"
            alt="crino&#0046;io - crino&#0046;io&#0032;&#0045;&#0032;Research&#0044;&#0032;connect&#0032;&#0038;&#0032;track&#0032;investors&#0032;effortlessly | Product Hunt"
            style={{ width: '250px', height: '54px' }}
            width="250"
            height="54"
          />
        </a>
      </div>

      {/* header row: title + reset */}
      <div className="mt-4 columns-2">
        <label className="block text-sm font-medium">
          {t('pageFindInvestors.fltrinvType')}
        </label>

        {(selectedTypes.length >= 2 || appliedIndustries || appliedKeyword || appliedLocation) && (
          <button
            className="block cursor-pointer text-sm text-stone-900 underline"
            onClick={resetFilters}
          >
            {t('pageFindInvestors.fltrinvReset')}
          </button>
        )}
      </div>

      {/* investor types list (scrollable) */}
      <div className="mt-5 overflow-y-auto max-h-40">
        {investorTypes.slice(0, 5).map((type, idx) => (
          <div key={idx} className="flex items-center mb-1">
            <Checkbox
              id={`investorType-${idx}`}
              className="mr-2"
              checked={selectedTypes.includes(type)}
              onCheckedChange={(checked) => handleTypeChange(!!checked, type)}
            />
            <label htmlFor={`investorType-${idx}`} className="text-sm text-stone-900">
              {type}
            </label>
          </div>
        ))}

        {showAllTypes &&
          investorTypes.slice(5).map((type, idx) => (
            <div key={idx + 5} className="flex items-center mb-1">
              <Checkbox
                id={`investorType-${idx + 5}`}
                className="mr-2"
                checked={selectedTypes.includes(type)}
                onCheckedChange={(checked) => handleTypeChange(!!checked, type)}
              />
              <label htmlFor={`investorType-${idx + 5}`} className="text-sm text-stone-900">
                {type}
              </label>
            </div>
          ))}
      </div>

      {/* BUTTON OUTSIDE THE SCROLL AREA — ALWAYS VISIBLE */}
      {!showAllTypes && investorTypes.length > 5 && (
        <div className="mt-2">
          <button
            className="cursor-pointer text-sm text-stone-900 underline"
            onClick={() => setShowAllTypes(true)}
          >
            {t('pageFindInvestors.fltrinvMoreTypes')}
          </button>
        </div>
      )}

      {/* keyword */}
      <div className="mt-6">
        <label className="mb-2 block text-sm font-medium text-stone-900">
          {t('pageFindInvestors.fltrinvSearchKwrd')}
        </label>
        <Input
          key={`kw-${appliedKeyword}`}              // force remount on clear/replace
          selectedValues={appliedKeyword ? [appliedKeyword] : []}
          onAddValue={(value) => applyKeyword(value)}
          onRemoveValue={() => clearKeyword()}
          placeholder={t('pageFindInvestors.fltrinvSearchKwrdPlaceholder')}
          className="w-full"
          // typing live text — it will be sent to filters_commit even without Enter/apply
          onChange={(e) => {
            const v = (e?.target?.value ?? '').toString();
            setLiveKeyword(v);
            hasUserInteracted.current = true;
            restartCommitCountdown();
          }}
          onInput={(e) => {
            const v = (e?.target?.value ?? '').toString();
            setLiveKeyword(v);
            hasUserInteracted.current = true;
            restartCommitCountdown();
          }}
          onKeyUp={(e) => {
            if (e.key !== 'Enter') return;
            const v = (liveKeyword || '').trim();
            if (!v) return clearKeyword();
            applyKeyword(v);
          }}
          onBlur={() => {
            const v = (liveKeyword || '').trim();
            if (!v) return clearKeyword();
            applyKeyword(v);
          }}
        />
      </div>

      {/* country */}
      <div className="mt-6">
        <label className="mb-2 block text-sm font-medium text-stone-900">
          {t('pageFindInvestors.fltrinvSearchOffice')}
        </label>
        <InputSearch
          key={`ct-${appliedLocation}`}            // remount on clear
          options={countries}
          selectedValue={appliedLocation}
          showCountryFlags
          onChange={(value) => {
            // Apply immediately when selected from dropdown
            hasUserInteracted.current = true;
            if ((value ?? '') === '') return clearLocation();
            applyLocation(value);
          }}
          placeholder={t('pageFindInvestors.fltrinvSearchOfficePlaceholder')}
          className="w-full"
          // If the component exposes input field events, support live mode
          onInput={(e) => {
            const v = (e?.target?.value ?? '').toString();
            setLiveCountry(v);
            hasUserInteracted.current = true;
            restartCommitCountdown();
          }}
          onKeyUp={(e) => {
            if (e.key !== 'Enter') return;
            const v = (liveCountry || '').trim();
            if (!v) return clearLocation();
            applyLocation(v);
          }}
          onBlur={() => {
            const v = (liveCountry || '').trim();
            if (!v) return clearLocation();
            applyLocation(v);
          }}
        />
      </div>

      {/* industry */}
      <div className="mt-6">
        <label className="mb-2 block text-sm font-medium text-stone-900">
          {t('pageFindInvestors.fltrinvSearchIndustry')}
        </label>
        <Input
          key={`ind-${appliedIndustries}`}         // remount on clear
          selectedValues={appliedIndustries ? [appliedIndustries] : []}
          onAddValue={(value) => applyIndustry(value)}
          onRemoveValue={() => clearIndustry()}
          placeholder={t('pageFindInvestors.fltrinvSearchIndustryPlaceholder')}
          className="w-full"
          onChange={(e) => {
            const v = (e?.target?.value ?? '').toString();
            setLiveIndustry(v);
            hasUserInteracted.current = true;
            restartCommitCountdown();
          }}
          onInput={(e) => {
            const v = (e?.target?.value ?? '').toString();
            setLiveIndustry(v);
            hasUserInteracted.current = true;
            restartCommitCountdown();
          }}
          onKeyUp={(e) => {
            if (e.key !== 'Enter') return;
            const v = (liveIndustry || '').trim();
            if (!v) return clearIndustry();
            applyIndustry(v);
          }}
          onBlur={() => {
            const v = (liveIndustry || '').trim();
            if (!v) return clearIndustry();
            applyIndustry(v);
          }}
        />
      </div>
      <AlertBox className="mt-12 mb-8" variant="yellow">{t('pageFindInvestors.t3')}</AlertBox>
    </>
  );
};

export default InvestorsFilter;
